import React, { useState } from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import { addEventDiscussionComment } from 'features/events/eventSlice'
import SpinnerSmall from 'components/SpinnerSmall'
import DiscussionCommentItem from './DiscussionCommentItem' 
import { VscCommentDiscussion } from "react-icons/vsc";

import { AiOutlineComment } from "react-icons/ai";
import { LuSend } from "react-icons/lu";
import { useTranslation } from 'react-i18next'


export default function Discussion() {
  const { t: translate } = useTranslation()
  const dispatch = useDispatch()
  const [newComment, setNewComment] = useState('')
  const { user } = useSelector((state) => state.auth) 
  const [inputFocus, setInputFocus] = useState(false)

  const { 
    singlePageEventData, 
    eventDiscussionComments,
    addEventDiscussionCommentLoading, 
    isEventDiscussionCommentsLoading

  } = useSelector((state) => state.events)

  const handleCommentSubmit = (event) => {
    event?.preventDefault()
    const comment = newComment.trim()
    if (!comment) {
      return
    }

    dispatch(addEventDiscussionComment({
      eventId: singlePageEventData._id,
      authorId: user._id, 
      authorName: user.fullName, 
      comment,
    }))

    setNewComment('')
    setInputFocus(false)

  }

  

  if(!user){
    return (
      <StatusPanel>
        <VscCommentDiscussion />
        <strong>{translate('events:discussion.join')}</strong>
        <span>{translate('events:discussion.login')}</span>
      </StatusPanel>
    )

  }else if(  singlePageEventData.discussionEnabled === false  ){
    return (
      <StatusPanel>
        <VscCommentDiscussion />
        <strong>{translate('events:discussion.disabled')}</strong>
        <span>{translate('events:discussion.disabledHelp')}</span>
      </StatusPanel>
    )
  }else { 
    return ( 
      <DiscussionContainer>
        <DiscussionHeader>
          <div>
            <HeaderIcon><VscCommentDiscussion /></HeaderIcon>
            <HeaderCopy>
              <h2>{translate('events:detail.discussion')}</h2>
              <p>{translate('events:discussion.commentCount', { count: eventDiscussionComments.length })}</p>
            </HeaderCopy>
          </div>
        </DiscussionHeader>
        <FormWrap
          as="form"
          onSubmit={handleCommentSubmit}
          onFocus={() => setInputFocus(true)}
          onBlur={() => setInputFocus(false)}
          $isActive={inputFocus}
          >
          <input 
            type="text" 
            placeholder={translate("web:events.addComment")}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            /> 
          <FormButton
            type="submit"
            disabled={!newComment.trim() || addEventDiscussionCommentLoading}
            aria-label={translate('events:discussion.post')}
            >
            {
              addEventDiscussionCommentLoading ? (<SpinnerSmall />) : (<LuSend size={15} />)
            }
          </FormButton>
        </FormWrap>
        <CommentsWrap>
            {
              // ensure comments are loaded before mapping
              (eventDiscussionComments.length > 0) ?
              (
                eventDiscussionComments.map((ele, index) => ( 
                    (ele.author !== null) && (
                      <DiscussionCommentItem
                        key = {ele._id}
                        data = {ele}
                        index = {index}
                        />                      
                    ) 
                ))              
              )
              : isEventDiscussionCommentsLoading ? (
                <LoadingState>
                  <AiOutlineComment />
                  <span>{translate('events:discussion.loading')}</span>
                </LoadingState>
                
              ) 
              :
              (
                <NoCommentsYet>
                  <VscCommentDiscussion />
                  <strong>{translate('events:detail.noComments')}</strong>
                  <span>{translate("web:events.noCommentsYet")}</span>
                </NoCommentsYet>
              )
  
            }
        </CommentsWrap>


      </DiscussionContainer>
    )
  }
}

const NoCommentsYet = styled.div`
  width: 100%;
  min-height: 190px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  color: #788488;
  text-align: center;
  svg { margin-bottom: 10px; color: #9aa6aa; font-size: 34px; }
  strong { color: #39464a; font-size: 14px; }
  span { margin-top: 5px; font-size: 12px; }
`

const StatusPanel = styled.div`
  width: 100%; min-height: 220px; display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 28px; border: 1px solid #e2e7e9; border-radius: 16px; background: #f8fafa; color: #788488; text-align: center;
  svg { margin-bottom: 12px; color: var(--themeColor); font-size: 36px; }
  strong { color: #344145; font-size: 15px; }
  span { max-width: 340px; margin-top: 6px; font-size: 12px; line-height: 1.45; }
`

const DiscussionContainer = styled.div`
  width: 100%; 
  display: flex;
  flex-direction: column; 
  position: relative;
  min-height: 300px;
  height: 100%; 
  scrollbar-width: none; 
  border: 1px solid #e1e7e9;
  border-radius: 16px;
  background: #ffffff;
  overflow: hidden;
  -ms-overflow-style: none; 

  &::-webkit-scrollbar { /* WebKit */
    width: 0;
    height: 0;
    background: transparent;

  }

`
const DiscussionHeader = styled.header`
  display: flex; align-items: center; justify-content: space-between; padding: 15px 16px 12px;
  > div { display: flex; align-items: center; gap: 10px; }
`

const HeaderIcon = styled.span`
  width: 38px; height: 38px; display: grid; place-items: center; border-radius: 11px;
  background: rgba(0, 180, 204, .11); color: var(--themeColor); font-size: 19px;
`

const HeaderCopy = styled.div`
  h2 { margin: 0; color: var(--lightThemeFontColor); font-size: 15px; line-height: 1.2; }
  p { margin: 3px 0 0; color: #7b878b; font-size: 11px; }
`

const FormWrap = styled.div` 
  width: calc(100% - 32px);
  min-height: 46px;
  margin: 0 16px 12px;
  display: flex;
  flex-direction: row; 
  align-items: center;
  justify-content: space-between;
  border-radius: 13px;
  padding: 5px 6px 5px 13px;
  background-color: #f3f6f7;
  border: 1px solid ${props => props.$isActive ? 'var(--themeColor)' : '#e1e7e9'};
  box-shadow: ${props => props.$isActive ? '0 0 0 3px rgba(0, 180, 204, .1)' : 'none'};
  transition: border-color .2s ease, box-shadow .2s ease;

  input{
    flex: 1;
    min-width: 0;
    padding: 7px 5px;
    border: none;
    outline: none;
    color: var(--lightThemeFontColor);
    font-size: 13px;
    background-color: transparent;
  }
`
const FormButton = styled.button`
  flex: 0 0 34px;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  height: 34px;
  width: 34px;
  border: 0;
  background-color: ${props => props.disabled ? '#e2e7e9' : 'var(--themeColor)'};
  color: ${props => props.disabled ? '#9aa4a8' : 'white'};
  border-radius: 10px;
  display: flex;
  justify-content: center;
  align-items: center; 

  &:hover{
    filter: ${props => props.disabled ? 'none' : 'brightness(.94)'};
  }

`
const CommentsWrap = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  padding: 4px 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: #f8fafa;
  max-height: 800px;
  overflow-y: auto;
  border-top: 1px solid #edf1f2;
  scrollbar-width: thin;
  scrollbar-color: #cbd4d7 transparent;
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track{
    background: transparent;
  }
`

const LoadingState = styled.div`
  min-height: 190px; display: flex; flex-direction: column; align-items: center; justify-content: center;
  color: #7d898d; font-size: 12px;
  svg { margin-bottom: 9px; color: var(--themeColor); font-size: 30px; }
`
